import React from 'react';
import { Link } from 'react-router-dom';
import './Services.css';

const services = [
  {
    id: '01',
    title: 'Web Development',
    color: 'fire',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
      </svg>
    ),
    tagline: 'Pixel-perfect, blazing-fast web experiences.',
    desc: 'We craft responsive, high-performance websites and web applications using the latest frameworks. Whether it\'s a landing page, e-commerce platform, or complex SaaS — we build it to convert and scale.',
    features: [
      'React, Next.js, Vue.js frontends',
      'Progressive Web Apps (PWA)',
      'E-commerce solutions',
      'CMS integration (Headless, WordPress)',
      'API development & integration',
      'Performance optimization & SEO',
    ],
  },
  {
    id: '02',
    title: 'Systems Development',
    color: 'ice',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="2" y="2" width="20" height="8" rx="2"/><rect x="2" y="14" width="20" height="8" rx="2"/>
        <path d="M6 6h.01M6 18h.01"/>
      </svg>
    ),
    tagline: 'Enterprise-grade systems built for the long haul.',
    desc: 'From internal management tools to full-scale ERP systems — we architect backend systems that power businesses. Secure, scalable, and built to handle growth.',
    features: [
      'ERP & CRM Systems',
      'Inventory & supply chain management',
      'HR & payroll systems',
      'Database architecture & optimization',
      'REST & GraphQL API design',
      'Microservices & cloud architecture',
    ],
  },
  {
    id: '03',
    title: 'App Development',
    color: 'fire',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <rect x="5" y="2" width="14" height="20" rx="2"/><path d="M12 18h.01"/>
      </svg>
    ),
    tagline: 'Mobile-first. User-obsessed. Always.',
    desc: 'We build native and cross-platform mobile apps that delight users. Our apps are fast, intuitive, and designed for maximum engagement — iOS, Android, or both.',
    features: [
      'React Native cross-platform apps',
      'Flutter development',
      'iOS & Android native apps',
      'App Store & Play Store publishing',
      'Push notifications & real-time features',
      'Offline capability & sync',
    ],
  },
  {
    id: '04',
    title: 'Digital Strategy',
    color: 'ice',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
      </svg>
    ),
    tagline: 'Strategy that turns vision into market share.',
    desc: 'Technology alone isn\'t enough. We partner with you on the strategy — from MVP planning to growth architecture — ensuring every digital decision serves your business goals.',
    features: [
      'Product roadmap & MVP planning',
      'Tech stack consultation',
      'Cloud infrastructure design (AWS, GCP)',
      'DevOps & CI/CD pipelines',
      'Security audits & penetration testing',
      'Performance monitoring & analytics',
    ],
  },
  {
    id: '05',
    title: 'UI/UX Design',
    color: 'fire',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
        <path d="M2 12h20"/>
      </svg>
    ),
    tagline: 'Interfaces people fall in love with.',
    desc: 'We design digital experiences that are not just beautiful but deeply functional. Every pixel, interaction, and flow is crafted to guide users naturally towards conversion.',
    features: [
      'User research & personas',
      'Wireframing & prototyping',
      'Design systems & component libraries',
      'Figma / Adobe XD designs',
      'Usability testing',
      'Brand identity & visual design',
    ],
  },
  {
    id: '06',
    title: 'Maintenance & Support',
    color: 'ice',
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
      </svg>
    ),
    tagline: 'We\'re in it with you — long after launch.',
    desc: 'We offer comprehensive maintenance plans to keep your systems running at peak performance. Bug fixes, updates, security patches, feature additions — we\'ve got you covered.',
    features: [
      '24/7 uptime monitoring',
      'Security patches & updates',
      'Performance optimization',
      'Feature enhancements',
      'Technical support & consultation',
      'Backup & disaster recovery',
    ],
  },
];

export default function Services() {
  return (
    <main className="services-page">
      {/* Hero */}
      <section className="page-hero">
        <div className="page-hero__glow" />
        <div className="container page-hero__inner">
          <div className="section-header__tag">Our Services</div>
          <h1 className="page-hero__title">
            Precision-Built <span className="text-fire">Solutions</span><br/>
            for Every Challenge
          </h1>
          <p className="page-hero__desc">
            From idea to deployment, we deliver with laser accuracy.
            Every service we offer is engineered to drive measurable outcomes.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-grid-section">
        <div className="container">
          <div className="services-grid">
            {services.map((svc, i) => (
              <div key={svc.id} className={`svc-card svc-card--${svc.color}`}>
                <div className="svc-card__num">{svc.id}</div>
                <div className="svc-card__icon">{svc.icon}</div>
                <h2 className="svc-card__title">{svc.title}</h2>
                <p className="svc-card__tagline">{svc.tagline}</p>
                <p className="svc-card__desc">{svc.desc}</p>
                <div className="svc-card__features">
                  {svc.features.map(f => (
                    <div key={f} className="svc-card__feature">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <div className="section-header__tag">Our Process</div>
            <h2 className="section-header__title">How We <span className="text-ice">Hit the Target</span></h2>
          </div>
          <div className="process-steps">
            {[
              { step: '01', title: 'Discovery', desc: 'We deep-dive into your business, goals, users, and competitive landscape to understand exactly what needs to be built.' },
              { step: '02', title: 'Strategy', desc: 'We architect the solution — tech stack, timeline, milestones, and success metrics. No guessing, just precision planning.' },
              { step: '03', title: 'Build', desc: 'Our team executes in agile sprints with regular demos. You\'re always in the loop, never in the dark.' },
              { step: '04', title: 'Launch', desc: 'Thorough testing, security checks, and a smooth deployment. We don\'t ship until it\'s perfect.' },
              { step: '05', title: 'Grow', desc: 'Post-launch support, analytics review, and continuous improvement. Your success is our ongoing mission.' },
            ].map((p, i) => (
              <div key={p.step} className="process-step">
                <div className="process-step__num">{p.step}</div>
                <div className="process-step__line" />
                <div className="process-step__content">
                  <h3>{p.title}</h3>
                  <p>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-banner">
        <div className="cta-banner__glow" />
        <div className="container cta-banner__inner">
          <div>
            <h2 className="cta-banner__title">Got a Project in Mind?</h2>
            <p className="cta-banner__sub">Let's scope it out together — free, no obligation.</p>
          </div>
          <div className="cta-banner__actions">
            <Link to="/contact" className="btn btn--white">Get a Free Quote →</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
